import React, {useEffect, useMemo, useState, useCallback, memo} from "react";
import { toast } from "react-toastify";
import { DeleteOutlined, EyeOutlined, WarningOutlined } from "@ant-design/icons";
import {
  Row,
  Col,
  Card,
  Select,
  Table,
  Button,
  Popconfirm,
  Popover,
  DatePicker,
  Tooltip,
} from "antd";
import { formatDateToBDD, formatDateToAPP } from "../../utils/tools";
import dayjs from "dayjs";

const dateFormat = "DD/MM/YYYY";
const { Option, OptGroup } = Select;
const { Column } = Table;
const { RangePicker } = DatePicker;


function FormApptSelectionnes(props) {
  const {
    lots = [],
    requete,
    requetesLots = [],
    isArchive,
    home,
    location,
    locations = [],
    parametre = {},
    currentOrdre,
    parcoursClients = [],
    deleteRequeteLot,
    afficherAppartement,
    saveRequeteLot,
    saveRecherche,
    saveLocation,
    updateLocation,
    syncRequete,
    onChangeTab,
  } = props;

  // ---------- State ----------
  const [stateRequete, setStateRequete] = useState(null);
  const [lotsAjoutables, setLotsAjoutables] = useState([]);
  const [selectedRows, setSelectedRows] = useState([]);
  const [suppressionOption, setSuppressionOption] = useState(false);
  const [dates, setDates] = useState([dayjs(), dayjs()]);

  // ---------- Derived data ----------
  const requetesLotsFiltrees = useMemo(
    () => requetesLots.filter((req) => req.requete_lot_type === 1),
    [requetesLots]
  );

  // ---------- init (ex-UNSAFE_componentWillReceiveProps + componentDidMount) ----------
  useEffect(() => {

    // TODO FORMAPPTSELECTIONNES condition de securité sinon plante
    if (requetesLots.length > 0 && lots.length > 0) {

      const existants = new Set(
        requetesLots
          .filter((req) => req.requete_lot_type === 1)
          .map((req) => req.requete_lot_lot?.lot_id)
      );

      const calculLotsAjoutables =
        lots?.filter((lot) => !existants.has(lot.lot_id)) ?? [];

      setLotsAjoutables(calculLotsAjoutables);
    }

    setStateRequete(requete || null);

  }, [lots, requete, requetesLots]);

  memo(function FooterAjoutRapide({isArchive, location, content,}) {

    if (location) return null; // on masque la colonne si une location existe
    return (
      <Col span={8} order={1}>
        <Popover placement="bottom" content={content} trigger="click">
          <Button disabled={isArchive} key="ajout-rapide" type="primary" size="small">
            Ajout Rapide
          </Button>
        </Popover>
      </Col>
    );
  });

// ---------- Handlers ----------
  const onConfirmDelete = useCallback(
    (appartement) => {
      if (!location || appartement.lot_id !== location.location_lot.lot_id) {
        deleteRequeteLot(stateRequete.requete_id, appartement.lot_id, 1).then(() =>
          toast.success("Le lot n'est plus lié à la requête !", { containerId: "A" })
        );
      } else {
        toast.error("Suppression impossible, une location existe pour ce lot !", {
          containerId: "A",
        });
      }
    },
    [deleteRequeteLot, location, stateRequete]
  );

  const onClickAfficher = useCallback(
    (id) => {
      const idLot = id.lot_id;
      const selection = requetesLots
        .filter((item) => item.requete_lot_lot?.lot_id !== 0)
        .map((item) => item.requete_lot_lot);
      const appartement = selection.find((item) => item.lot_id === idLot);
      afficherAppartement(appartement);
    },
    [requetesLots, afficherAppartement]
  );

  const onDateRangeChange = useCallback((e) => {
    setDates(e ? [e[0], e[1]] : [null, null]);
  }, []);

  const onSelectLot = useCallback(
    (id) => {
      const etats = parametre.parametre_blocage_option ? ["r", "o", "b", "n"] : ["r", "b", "n"];

      const periodeIndispo = locations.some(
        (item) =>
          item.location_lot.lot_id === id &&
          (
            dayjs(dates[0]).isBetween(
              dayjs(item.location_date_debut, "YYYY-MM-DD"),
              dayjs(item.location_date_fin, "YYYY-MM-DD"),
              undefined,
              "[)"
            ) ||
            dayjs(dates[1]).isBetween(
              dayjs(item.location_date_debut, "YYYY-MM-DD"),
              dayjs(item.location_date_fin, "YYYY-MM-DD"),
              undefined,
              "(]"
            )
          ) &&
          etats.includes(item.location_etat)
      );

      if (!periodeIndispo) {
        saveRequeteLot({
          requete_lot_requete_id: requete.requete_id,
          requete_lot_lot_id: id,
          requete_lot_date_debut: dates[0].format("YYYY-MM-DD"),
          requete_lot_date_fin: dates[1].format("YYYY-MM-DD"),
          requete_lot_type: 1,
        })
          .then(() => {
            if (!stateRequete?.requete_recherche) {
              saveRecherche({
                recherche_alur: false,
                recherche_court_terme: true,
                recherche_long_terme: false,
                recherche_date_debut: dates[0].format("YYYY-MM-DD"),
                recherche_date_fin: dates[1].format("YYYY-MM-DD"),
                recherche_type_contrat: null,
                recherche_prix_min: null,
                recherche_prix_max: null,
                recherche_couchages: null,
                recherche_surfaces: null,
                recherche_type_lot: null,
                recherche_requete_id: requete.requete_id,
                recherche_equipement: [],
                recherche_quartier: [],
                recherche_metro: [],
              });
            }
            toast.success("Lot ajouté !", { containerId: "A" });
          })
          .catch(() =>
            toast.error("Erreur lors de l'ajout du lot !", {
              containerId: "A",
            })
          );
      } else {
        toast.error("Les dates sélectionnées ne sont pas disponibles pour ce lot", {
          containerId: "A",
        });
      }
    },
    [dates, locations, parametre.parametre_blocage_option, requete, saveRequeteLot, saveRecherche, stateRequete]
  );

  const mettreOption = useCallback(() => {
    if (selectedRows.length !== 1) {
      toast.error("Veuillez sélectionner un seul appartement !", { containerId: "A" });
      return;
    }

    const item = selectedRows[0];
    const getTypeContrat = (lot) =>
      lot.lot_court_terme && !lot.lot_alur && !lot.lot_long_terme ? 3 : null;

    if (dayjs(item.requete_lot_date_fin).diff(dayjs(item.requete_lot_date_debut)) <= 0) {
      toast.error("Mise en option impossible, la durée sélectionnée est de 0 jour !", {
        containerId: "A",
      });
      return;
    }

    const jsonData = {
      location_date_creation: formatDateToBDD(),
      location_date_debut: item.requete_lot_date_debut,
      location_date_fin: item.requete_lot_date_fin,
      location_acompte_propose: 0.0,
      location_remarques: "",
      location_desiderata: "",
      location_fin_option: "/",
      location_etat: "o",
      location_nb_bebes: 0,
      location_nb_adultes: 0,
      location_nb_enfants: 0,
      location_montant_propose: 0,
      location_pourcentage_acompte: 0,
      location_heure_arrivee: "10:00",
      location_heure_depart: "11:45",
      location_entree: true,
      location_sortie: false,
      location_menage: true,
      location_type_contrat: getTypeContrat(item.requete_lot_lot),
      location_remise_fidelite: true,
      location_lot_id: item.requete_lot_lot.lot_id,
      location_requete_id: stateRequete.requete_id,
      location_bail_signe: false,
      location_info_sejour: false,
      location_chauffeur_reserve: false,
    };

    let checkOption = true;
    if (parametre.parametre_parcours_client && stateRequete?.requete_groupe) {
      const etape = parcoursClients.find(
        (p) =>
          p.parcours_client_groupe.groupe_id === stateRequete.requete_groupe.groupe_id &&
          p.parcours_client_option === true
      );
      checkOption = (currentOrdre ?? 0) + 1 >= (etape?.parcours_client_ordre ?? Infinity);
    }

    if (checkOption) {
      saveLocation(jsonData).then(() => {
        setSuppressionOption(true);
        syncRequete({ ...requete, update: true });
        onChangeTab("3");
      });
    } else {
      toast.error("La mise en option n'est pas disponible pour l'étape en cours !", {
        containerId: "A",
      });
    }
  }, [
    selectedRows,
    stateRequete,
    parametre.parametre_parcours_client,
    parcoursClients,
    currentOrdre,
    saveLocation,
    syncRequete,
    requete,
    onChangeTab,
  ]);

  const enleverOption = useCallback(() => {
    if (selectedRows.length !== 1) return;
    const loc = { ...location };
    if (loc) {
      loc.location_lot_id = loc.location_lot.lot_id;
      loc.location_requete_id = loc.location_requete.requete_id;
      loc.location_etat = "a";
      loc.location_calendrier_bloque = false;
      updateLocation(loc, loc.location_id)
        .then(() => {
          syncRequete({ ...requete, update: true });
          setSuppressionOption(false);
          toast.success(`L'option' a été supprimée`, { containerId: "A" });
        })
        .catch((err) => console.log("err", err));
    }
  }, [selectedRows, location, updateLocation, syncRequete, requete]);

  const proposer = useCallback(() => onChangeTab("2", null, "Proposition"), [onChangeTab]);

  const setBackgroundColor = useCallback(
    (item) => {
      if (location && item.requete_lot_lot.lot_id === location.location_lot.lot_id) {
        switch (location.location_etat) {
          case "r":
            return "row-reserved-bg";
          case "o":
            return "row-option-bg";
          case "a":
            return "row-canceled-bg";
          default:
            return "row-no-bg";
        }
      }
      return "row-no-bg";
    },
    [location]
  );

  const getDateDebut = useCallback(
    (date, row) => {
      if (location && row.requete_lot_lot.lot_id === location.location_lot.lot_id) {
        date = location.location_date_debut;
      }
      return formatDateToAPP(date);
    },
    [location]
  );

  const getDateFin = useCallback(
    (date, row) => {
      if (location && row.requete_lot_lot.lot_id === location.location_lot.lot_id) {
        date = location.location_date_fin;
      }
      return formatDateToAPP(date);
    },
    [location]
  );

  const order = useMemo(
    () => ({
      option: home ? 3 : 3,
      proposer: home ? 2 : 2,
      ajoutRapide: home ? 1 : 1,
    }),
    [home]
  );

  const rowSelection = {
    type: "checkbox",
    onChange: (_selectedRowKeys, rows) => {
      let option = false;
      if (rows.length === 1 && location) {
        if (rows[0].requete_lot_lot.lot_id === location.location_lot.lot_id) {
          if (location.location_etat === "o") option = true;
        }
      }
      setSelectedRows(rows);
      setSuppressionOption(option);
    },
  };

  const content = (
    <>
      <Row gutter={12}>
        <Col span={22}>
          <RangePicker
            disabled={isArchive}
            format={dateFormat}
            getPopupContainer={(trigger) => trigger.parentElement}
            onCalendarChange={onDateRangeChange}
          />
        </Col>
        {dates[0]?.isBefore(dayjs(), "day") && (
          <Col span={2}>
            <Tooltip title="Dates antérieures">
              <WarningOutlined />
            </Tooltip>
          </Col>
        )}
      </Row>
      <Row style={{ marginTop: 10 }}>
        <Col span={22}>
          <Select
            disabled={isArchive}
            showSearch
            optionLabelProp="label"
            placeholder="Lots"
            style={{ width: "100%" }}
            onSelect={onSelectLot}
            dropdownRender={(menu) => <div>{menu}</div>}
            filterOption={(input, option) =>
              option.label.toLowerCase().indexOf(input.toLowerCase()) >= 0
            }
          >
            <OptGroup label="Lots">
              {lotsAjoutables?.map((item, i) => (
                <Option key={i} value={item.lot_id} label={`${item.lot_reference}`}>
                  <Row>
                    <Col sm={8}>{`${item.lot_reference}`}</Col>
                  </Row>
                </Option>
              ))}
            </OptGroup>
          </Select>
        </Col>
      </Row>
    </>
  );

  const footer =
    stateRequete && (
      <div>
        <Row gutter="20" justify="space-between" align="middle">
          <Col order={order.option}>
            {suppressionOption ? (
              <Button
                disabled={isArchive || selectedRows.length === 0}
                key={"1"}
                type="primary"
                size="small"
                //style={{ background: "#ffbf00", borderColor: "#ffbf00" }}
                onClick={enleverOption}
              >
                Enlever Option
              </Button>
            ) : (
              <Popconfirm
                title="Confirmez vous la mise en option pour ces dates ?"
                disabled={isArchive || location || selectedRows.length === 0}
                onConfirm={mettreOption}
              >
                <Button
                  disabled={isArchive || location || selectedRows.length === 0}
                  key={"1"}
                  type="primary"
                  size="small"
                >
                  Mettre Option
                </Button>
              </Popconfirm>
            )}
          </Col>
          <Col order={order.proposer}>
            <Button
              disabled={isArchive || selectedRows.length === 0}
              key={"2"}
              type="primary"
              size="small"
              onClick={proposer}
            >
              Courriel
            </Button>
          </Col>
          {!location && (
            <Col order={order.ajoutRapide}>
              <Popover placement="bottom" content={content} trigger="click">
                <Button disabled={isArchive} key={"4"} type="primary" size="small">
                  Ajout Rapide
                </Button>
              </Popover>
            </Col>
          )}
        </Row>
      </div>
    );

  return (
    <Card className="requete-card" disabled={isArchive}>
      <Table
        size='small'
        rowSelection={rowSelection}
        rowClassName={(record) => setBackgroundColor(record)}
        footer={() => footer}
        style={{ minHeight: home ? 200 : 220 }}
        dataSource={requetesLotsFiltrees}
        showSorterTooltip={false}
        pagination={false}
        scroll={{ x: "max-content", y: home ? 200 : 145 }}
        rowKey="key"
      >
        <Column
          width={70}
          title="Réf."
          dataIndex="requete_lot_lot"
          key="requete_lot_lot.lot_id"
          render={(lot) => <>{lot.lot_reference}</>}
        />
        <Column
          title="Date"
          key="periode_date"
          dataIndex="requete_lot_date_debut"
          render={(dateDebut, row) => {
            const dateFin = row.requete_lot_date_fin;
            if (!dateDebut && !dateFin) return "";
            const debut = dateDebut ? getDateDebut(dateDebut, row) : "";
            const fin = dateFin ? getDateFin(dateFin, row) : "";
            if (debut && fin) return `Du ${debut} au ${fin}`;
            if (debut) return `Du ${debut}`;
            if (fin) return `Au ${fin}`;
            return "";
          }}
        />
        <Column
          title="Actions"
          key="requete_lot_lot"
          dataIndex="requete_lot_lot"
          align={"right"}
          render={(appartement) => (
            <>
              <Button
                size="small"
                disabled={isArchive} onClick={() => onClickAfficher(appartement)} style={{ marginRight: 10 }}>
                <EyeOutlined />
              </Button>
              <Popconfirm
                disabled={isArchive}
                title="Supprimer cette recherche de la liste ?"
                onConfirm={() => onConfirmDelete(appartement)}
              >
                <Button size='small' disabled={isArchive}>
                  <DeleteOutlined />
                </Button>
              </Popconfirm>
            </>
          )}
        />
      </Table>
    </Card>
  );
}

export default FormApptSelectionnes;
